import React, { useEffect, useMemo, useState, useRef } from 'react';
import { GetStaticProps, GetStaticPaths } from 'next';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { generateImage } from 'utils/image.utils';
import LazyHydrate from 'react-lazy-hydration';
import NoResult from 'components/Icons/NoResult';
import CategoryBanner, { CategoryBannerProps } from 'components/CategoryBanner/CategoryBanner';
import PostitWidget, { PostitWidgetProps } from 'components/Others/PostitWidget';
import { getAccessorieName } from 'functions/getProductName';
import Breadcrumb from 'components/Breadcrumb/Breadcrumb';
import ProductCard from 'components/ProductCard/ProductCard';
import { getLocalCurrency } from 'functions/currency';
import useWindowSize from 'hooks/useWindowSize';
import { addDataLayer } from 'lib/ga'; 
import CLFilter from 'components/ContactLenses/CLFilter';
import { bundleProductTagsMap } from 'utils/common.utils';
import { wcApiDataService } from 'functions/wcApi';
import { getContactLensStaticProps } from 'utils/contactlens.utils';
import CategorySeoText from 'components/Others/CategorySeoText';
import FAQJson from 'components/Others/FAQJson';
import { ContactLensesPageProps } from 'constants/dataTypes';
import ProductCardwLensColour from 'components/ProductCard/ProductCardwLensColour';
import GlobalSeoMetaTags from 'components/GlobalSeoMetaTags/GlobalSeoMetaTags';
import { getContactLensAltImageText } from 'functions/getSeoContentByCategorySlug';
import CategoryBannerImageComponent from 'components/CategoryBannerImage/CategoryBannerImage';
import { CategoryBannerImage } from 'constants/dataTypes';
const RecommendedProductsSlider = dynamic(
  () => import('components/Others/RecommendedProductsSlider')
);


export default function ContactLenses({
  categories,
  populatedNames,
  filterParams,
  seoBlock,
  seoMetaTags,
  categoryBannerProps,
  categoryBannerImages
}: ContactLensesPageProps) {
  const { width } = useWindowSize();
  const router = useRouter();
  const [activePanel, setActivePanel] = useState<any | null>(null);
  const [cTable, setCTable] = useState<any>();
  
  const [maxPlaceholderPrice, setMaxPlaceholderPrice] = useState(0);
  const [minPlaceholderPrice, setMinPlaceholderPrice] = useState(0);
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [minPrice, setMinPrice] = useState<string>('');
  const minP = useRef<any>(null);
  const maxP = useRef<any>(null);

  // Create an indexed map of banners
  const indexedBanners = useMemo(() => {
    const bannerMap = new Map<number, CategoryBannerImage>();
    
    categoryBannerImages.forEach(banner => {
      if (banner.bannerType === 'small-square') {
        bannerMap.set(banner.orderIndex, banner);
      } else {
        // For large rectangles, calculate the next multiple of 3
        const nextMultipleOfThree = Math.ceil(banner.orderIndex / 3) * 3 + 1;
        bannerMap.set(nextMultipleOfThree, banner);
      }
    });
    
    return bannerMap;
  }, [categoryBannerImages]);

  // Track the number of category banner images
  let categoryBannerImageCount = 0;

  const postitWidgetProps: PostitWidgetProps[] = categories.meta?.postit_notes;

  const [isMobileFilterEnabled, setIsMobileFilterEnabled] = useState(false)
  
  const sortBy: any = [{name:"Name: A-Z", type:"A-Z"}, {name:"Name: Z-A", type: "Z-A"}, {name:"Price: Low to High", type: "cheapest"}, {name:"Price: High to Low", type:"highest"}];
  const priceRange: any = [{option:'25$ - 50$', val:[25,50]},{option:'50$ - 75$', val:[50,75]},{option:'75$ - 100$', val:[75,100]},{option:'100$ - 125$', val:[100,125]},{option:'125$ - 150$', val:[125,150]},{option:'150$ +', val:[150]}]
  const emptySelectedFilter:any = {
    'pa_contact-lens-brand':[],
    'pa_contact-lens-manufacturer':[],
    'pa_contact-lens-colour':[],
    'pa_frequency':[],
    'pa_contact-lens-type':[],
    'pa_pack-size': [] 
  }

  const urlTypes:any = {
    "brand":"pa_contact-lens-brand",
    "frequency":"pa_frequency",
    "type":"pa_contact-lens-type"
  }

  const [firstEntryPoint, setFirstEntryPoint] = useState<any>([urlTypes[Object.keys(filterParams)[0]]]);
  const [selectedFilters, setSelectedFilters] = useState<any>(Object.assign({},emptySelectedFilter,{[urlTypes[Object.keys(filterParams)[0]]]:[filterParams[Object.keys(filterParams)[0]]]}))
  const [selectedFilterLength, setSelectedFiltersLength] = useState<any>(null);
  const [selectedSort, setSelectedSort] = useState(2)
  const [selectedPriceFilters, setSelectedPriceFilters] = useState<any>([])
  const [searchValue, setSearchValue] = useState<string>('')
  const [bundleFilter, setBundleFilter] = useState<boolean>(filterParams.bundle ? true : false)
  const handleSearchChange = (e:any) => {
    setSearchValue(e.target.value)
  };

  const handleCustomPriceFilter = () => {
    const min = parseInt(minP?.current?.value);
    const max = parseInt(maxP?.current?.value);
    if(!min || !max) return
    setMinPrice(''+min);
    setMaxPrice(''+max);
    setSelectedPriceFilters([{option:`${min}$ - ${max}$`, val:[min, max]}]);
  }

  useEffect(() => {
    if (router && router.locale === 'nz') {
      location.reload();
    }
  }, [router]);
  
  useEffect(()=> {
    const len = Object.keys(selectedFilters).map((item) => selectedFilters[item].length).reduce((a, b) => a + b);
    setSelectedFiltersLength(len)
    if(len == 0){
      setFirstEntryPoint(null)
      router.replace('/shop/contact-lenses')
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[selectedFilters])

  useEffect(() => {
    if(width > 1024){
      setIsMobileFilterEnabled(false)
    }
  }, [width])

  useEffect(() => {
    const type = isMobileFilterEnabled? 'hidden' : 'auto'
    document.body.style.overflow = type
  }, [isMobileFilterEnabled]);

  const data = [
    {
      id: 1,
      href: '',
      text: 'Dresden Vision'
    },
    {
      id: 2,
      href: 'shop',
      text: 'Shop'
    },
    {
      id: 3,
      href: `shop/contact-lenses`,
      text: `Contact Lenses`
    },
    {
      id: 4,
      href: `shop/contact-lenses/${router.query.frequency}`,
      text: `${router.query.frequency && populatedNames[(router.query.frequency as string)]} Contact Lenses`
    }
  ];
  const [categoryProductsForPosition, setCategoryProductsForPosition] =
    useState<any>();

  const handleMobileFilter = () => {
    setIsMobileFilterEnabled(!isMobileFilterEnabled)
  }

  const focusInputField = (input:any) => {
    if (input) {
      setTimeout(() => {input.focus()}, 10);
    }
  };

  const filteredProducts = useMemo(() => {
    const productList: any[] = [];
    const filteredCountTable:any = {};
    const priceArr:any[] = [];

    [...(categories.products)].map((prod: any) => {
      productList.push(prod);
    });

    const filtered = productList.filter(product => {
      const foundMap = selectedPriceFilters.map((e:any) => {
        if(e.val[1]){
          if(parseInt(product.price) >= e.val[0] && parseInt(product.price) <= e.val[1]){
            return true
          }
        }else if(parseInt(product.price) >= e.val[0]){
          return true
        }
      });
      
      
      return foundMap.length > 0 ? foundMap.some((e:any) => e == true): true
    }).filter(product => {     
      return Object.keys(Object.fromEntries(Object.entries(selectedFilters).filter(([_, v]: [any, any]) => v.length > 0))).reduce((acc, filter) => {
        const filterValues = selectedFilters[filter];
        const productAttributes = product.attributes.filter((e: any) => e.slug === filter);

        if (!filterValues || !productAttributes.length) return acc;

        // Check if any of the filter values match any of the product's attribute options
        const found = filterValues.some((fv: any) =>
          productAttributes.some((attr: { options: string[] }) => attr.options.includes(fv))
        );

        return acc && found;
      }, true);
    }).filter(product => {
      if(!bundleFilter) return true;

      const hasAttribute = (attributes: any, slug: string) => {
        return attributes.filter((item: any) => item.slug === slug).length > 0;
      }
      if(hasAttribute(product.attributes, 'pa_bundle-box-count')) return true;
    }).sort((a: any ,b: any) => {
      a.attributes.forEach((e:any) => { 
        e.options.forEach((item:any) => {
          filteredCountTable[item] = parseInt(filteredCountTable[item])+1 || 1;
        }) 
      })
      if(sortBy[selectedSort].type == 'A-Z') return a.name.localeCompare(b.name)
      if(sortBy[selectedSort].type == 'Z-A') return b.name.localeCompare(a.name)
      if(sortBy[selectedSort].type == 'cheapest'){
        const keyA = parseInt(a.price);
        const keyB = parseInt(b.price);
        if (keyA >= keyB) return 1;
        if (keyA < keyB) return -1;
        return 0;
      }
      if(sortBy[selectedSort].type == 'highest'){
        const keyA = parseInt(a.price);
        const keyB = parseInt(b.price);
        if (keyA >= keyB) return -1;
        if (keyA < keyB) return 1;
        return 0;
      }

    }).filter(product => {
      if(searchValue.length == 0) return true;
      return product.name.toLocaleLowerCase().indexOf(searchValue.toLocaleLowerCase()) > -1
    }).filter(product => {
      if(!bundleFilter) return true;

      const hasAttribute = (attributes: any, slug: string) => {
        return attributes.filter((item: any) => item.slug === slug).length > 0;
      }
      if(hasAttribute(product.attributes, 'pa_bundle-box-count')) return true;
    })
    filtered.forEach((item) => {
      priceArr.push(item.price);
    })

    setMaxPlaceholderPrice(() => Math.max(...priceArr));
    setMinPlaceholderPrice(() => Math.min(...priceArr));
    setCTable(() => filteredCountTable);
    
    return filtered;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFilters, selectedSort, selectedPriceFilters, searchValue, router.query, bundleFilter]);

  const populatedAttributes = useMemo(() => {
    const mainAttributes:any = {};
    const filteredAttributes:any = {};
    const countTable:any = {};
    const filteredCountTable:any = {};
    [...(categories.products)].map((item) => {
      item.attributes.forEach((e:any) => {
        if(!mainAttributes[e.slug]){
          mainAttributes[e.slug] = e.options;
          
        }else{
          if(!mainAttributes[e.slug].includes(e.options?.[0])){
            mainAttributes[e.slug] =[...mainAttributes[e.slug], ...e.options]
          }
        }

        e.options.forEach((item:any) => {
          countTable[item] = parseInt(countTable[item]) + 1 || 1;
        }) 
        
      })
    })

    filteredProducts.map((item:any) => {
      
      item.attributes?.forEach((e:any) => {
        if(!filteredAttributes[e.slug]){
          filteredAttributes[e.slug] = e.options;
          
        }else{
          if(!filteredAttributes[e.slug].includes(e.options?.[0])){
            filteredAttributes[e.slug] =[...filteredAttributes[e.slug], ...e.options]
          }
        }

        e.options.forEach((item:any) => {
          filteredCountTable[item] = parseInt(filteredCountTable[item])+1 || 1;
        }) 
        
      })
    })


    
    mainAttributes.countTable = countTable;
    setCTable(filteredCountTable);
    return mainAttributes
  }, [categories.products, filteredProducts])


  useEffect(() => {
    var categoryProducts: any = [];
    var position = 1;

    categories.products &&
      [...categories.products]
        .sort((a: any, b: any) => {
          const keyA = parseInt(a.sortIndex?.index);
          const keyB = parseInt(b.sortIndex?.index);
          if (keyA > keyB) return 1;
          if (keyA < keyB) return -1;
          return 0;
        }).map((prod: any) => {
          categoryProducts.push({
            name: getAccessorieName(prod.name),
            price: prod?.price,
            category: categories?.name,
            list: 'Shop',
            position: position
          });
          position++;
        });



    setCategoryProductsForPosition(categoryProducts);
    addDataLayer({
      event: 'ImpCompleted',
      ecommerce: {
        currencyCode: getLocalCurrency(router.locale),
        impressions: categoryProducts
      }
    });
    setSelectedFilters(Object.assign({},emptySelectedFilter,{[urlTypes[Object.keys(filterParams)[0]]]:[filterParams[Object.keys(filterParams)[0]]]}))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router]);

  if (router.isFallback) {
    return (
      <div className="flex h-[70vh] items-center justify-center">
        Loading...
      </div>
    );
  }

  return (
    <div>
     <Head>
        <title>{`${populatedNames[(router.query.frequency as string)]} Contact Lenses | Dresden Vision`}</title>
        <meta name="twitter:title" content={`${populatedNames[(router.query.frequency as string)]} Contact Lenses | Dresden Vision`} />
        <meta name="og:title" content={`${populatedNames[(router.query.frequency as string)]} Contact Lenses | Dresden Vision`} />
        <meta
          name="description"
          content={`Order ${populatedNames[(router.query.frequency as string)]} contact lenses quickly: top ${populatedNames[(router.query.frequency as string)]} contact lenses brands online now! Shop safely ${populatedNames[(router.query.frequency as string)]} contact lenses.`}
        />
        <meta
          property="og:description"
          content={`Order ${populatedNames[(router.query.frequency as string)]} contact lenses quickly: top ${populatedNames[(router.query.frequency as string)]} contact lenses brands online now! Shop safely ${populatedNames[(router.query.frequency as string)]} contact lenses.`}
        />
        <meta
          name="twitter:description"
          content={`Order ${populatedNames[(router.query.frequency as string)]} contact lenses quickly: top ${populatedNames[(router.query.frequency as string)]} contact lenses brands online now! Shop safely ${populatedNames[(router.query.frequency as string)]} contact lenses.`}
        />
      </Head>
      <CategoryBanner {...categoryBannerProps} frequency={populatedNames[(router.query.frequency as string)]} breadcrumbItems={data} />
      <div className={`mx-auto max-w-[1224px] bg-white px-4 pt-4`}>
        <div>
          <CLFilter isMobileFilterEnabled={isMobileFilterEnabled}
            emptySelectedFilter={emptySelectedFilter}
            populatedAttributes={populatedAttributes}
            activePanel={activePanel}
            setSelectedFilters={setSelectedFilters}
            setFirstEntryPoint={setFirstEntryPoint}
            setMaxPrice={setMaxPrice}
            setMinPrice={setMinPrice}
            setSearchValue={setSearchValue}
            setSelectedPriceFilters={setSelectedPriceFilters}
            setIsMobileFilterEnabled={setIsMobileFilterEnabled}
            handleCustomPriceFilter={handleCustomPriceFilter}
            setActivePanel={setActivePanel}
            selectedFilters={selectedFilters}
            selectedPriceFilters={selectedPriceFilters}
            firstEntryPoint={firstEntryPoint}
            populatedNames={populatedNames}
            selectedFilterLength={selectedFilterLength}
            minP={minP}
            minPrice={minPrice}
            maxPrice={maxPrice}
            maxP={maxP}
            focusInputField={focusInputField}
            searchValue={searchValue}
            handleMobileFilter={handleMobileFilter}
            handleSearchChange={handleSearchChange}
            minPlaceholderPrice={minPlaceholderPrice}
            maxPlaceholderPrice={maxPlaceholderPrice}
            sortBy={sortBy}
            priceRange={priceRange}
            cTable={cTable}
            selectedSort={selectedSort}
            setSelectedSort={setSelectedSort}
            bundleFilter={bundleFilter}
            setBundleFilter={setBundleFilter}></CLFilter>

        </div>
        <div className="text-center text-brand-grey450 text-base font-normal mb-4">{filteredProducts.length} Contact Lenses</div>
        {filteredProducts.length == 0 && <div className="max-w-xs mx-auto pt-20 items-center text-center">
          <span className=" text-lg font-normal text-brand-grey450 "><div className=" w-[180px] mx-auto"><NoResult /></div>No results found. Please clear filters.</span>
        </div>}
        <div className={`mt-[24px] grid grid-cols-1 gap-y-[20px] md:grid-cols-2 md:gap-x-[15px] md:gap-y-[30px] shop:grid-cols-3`}>
          
          {filteredProducts.map((prod: any, index: number) => {
            const currentPosition = index + 1 + categoryBannerImageCount;
            const bannerImage = indexedBanners.get(currentPosition);
            if(bannerImage) {
              categoryBannerImageCount++;
            }
            return (
              <React.Fragment key={index}>
                {bannerImage && (
                  <div className={bannerImage.bannerType === 'large-rectangle' ? 'col-span-full' : undefined}>
                    <CategoryBannerImageComponent banner={bannerImage} width={width} />
                  </div>
                )}
                <LazyHydrate whenVisible key={index + '_card_lh'}>
                  <ProductCardwLensColour
                    onClickPoroduct={() => {
                      addDataLayer({
                        event: 'productClick',
                        ecommerce: {
                          click: {
                            actionField: { list: 'category' },
                            products: [
                              { 
                                name: getAccessorieName(prod.name),
                                price: prod?.price,
                                category: categories?.name,
                                list: 'Category',
                                position: categoryProductsForPosition.filter(
                                  (item: any) =>
                                    item.name === getAccessorieName(prod.name)
                                )[0].position
                              }
                            ]
                          }
                        }
                      });
                    }}
                    key={index + '_card'}
                    width={width}
                    data={{
                      images: prod.meta.product_images,
                      title: getAccessorieName(prod.name),
                      price: prod?.meta.price,
                      regularPrice: prod?.meta.regular_price,
                      href: `/shop/${categories.slug}/${prod.slug}`,
                      prio: width > 600 ? index <= 1 : index == 0,
                      additionalTags: prod.attributes.filter((item:any) => item.slug == 'pa_frequency' || item.slug == 'pa_pack-size' || (item.slug == 'pa_contact-lens-type' && item.options?.[0] != 'sphere')).map((i:any) => (i.options.map((name:any) => populatedNames[name]).join(', ') || i.options[0]+' Lenses')).sort((a:any,b:any) => b.localeCompare(a)),
                      productTags: prod.tags && prod.tags.length > 0 ? prod.tags.map((item: any) => bundleProductTagsMap[item.slug] || item.slug) : [],
                      discountTag: prod?.meta.price != prod?.meta.regular_price,
                    }}
                  />
                </LazyHydrate>
              </React.Fragment>
            )
          })}
        </div> 
        <LazyHydrate whenVisible>
          <RecommendedProductsSlider />
        </LazyHydrate>
        <LazyHydrate whenVisible>
          <CategorySeoText seoBlock={seoBlock} />
        </LazyHydrate>
        <FAQJson faq={seoBlock.faq} />
        <GlobalSeoMetaTags  seoMetaTags={seoMetaTags} />

      </div>
    </div>
  );
}
export const getStaticPaths: GetStaticPaths = async () => {
    
    const paths: any = [];
    if (process.env.NEXT_PUBLIC_ENV !== 'production') {
      return {
        paths: [],
        fallback: 'blocking',
      }
    }
    var contactLensParams:any = await wcApiDataService('paths/term/frequency', '', '');

      if (!contactLensParams) {
        return {
          paths,	  
          fallback: 'blocking'
        };
      }

      for (const [country, data] of Object.entries(contactLensParams) as any) {
        data['frequency'].map((itemSlug: any) => {
        
            paths.push({
              params: {
                frequency: itemSlug
              },
              locale: country
              });
    
        });
      }

  
      return {
        paths,
        fallback: 'blocking'
      };

  }
export const getStaticProps: GetStaticProps = async ({params, locale }) => {
  
  return getContactLensStaticProps(locale, params);

}
 
